import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { CheckCircle2, Lock, Shield } from "lucide-react";

import useTimer from "../../hooks/useTimer";
import bbbBadge from "../../../assets/new_ui_images/bbb-badge.webp";
import { Button } from "../../common/customDesignedComponents/button";
import soc2Badge from "../../../assets/new_ui_images/soc2-type2-badge.png";
import HubspotCookieBreakerInput from "../../common/HubspotCookieBreakerInput";
import { TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS } from "../../helpers/constants";
import oneMillionBadge from "../../../assets/new_ui_images/one-million-usd.webp";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../common/customDesignedComponents/inputOTP";

const benefits = [
  "Verify your identity with a one-time code",
  "Prevent fraudulent signups using fake numbers",
  "Send urgent alerts about suspicious property activity",
  "Contact you quickly if action is needed",
  "Protect your privacy — used only for security and alerts, never shared",
];

const VerifyPhone = ({
  onSubmit,
  handleChange,
  otp,
  otpErr,
  otpSucess,
  phn_num,
  resendOTPButtonClick,
}) => {
  //initializes the timer states for hook
  const { timer, isTimerActive, startTimer, formatTimeForResendOTP } =
    useTimer(120);

  // Mask phone number to show only last 4 digits
  const maskPhone = (phoneNumber: string) => {
    if (!phoneNumber || phoneNumber.length < 4) return "*** *** ****";
    const lastFour = phoneNumber.slice(-4);
    return `* *** *** ${lastFour}`;
  };

  return (
    <>
      <Helmet>
        <title>
          Verify Phone | EquityProtect - Complete Your Account Setup
        </title>
        <meta
          name="description"
          content="Verify your mobile phone number to continue with your EquityProtect account setup and start protecting your home equity."
        />
      </Helmet>

      <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto adbox_wrapper">
          {/* Left Column - Advertisement Box */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-32 adbox_width"
          >
            <div className="bg-gradient-teal rounded-2xl p-8 md:p-10 text-white">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-10 w-10 text-white" />
                <h2 className="text-2xl md:text-3xl font-display font-medium">
                  Mobile Validation
                </h2>
              </div>

              <p className="text-lg text-white/90 font-medium mb-2">
                Why We Ask for Your Mobile Number
              </p>
              <p className="text-white/80 text-sm mb-8">
                Your mobile number helps keep your account and property secure
                and allows us to reach you quickly if there is a potential risk.
              </p>

              <ul className="space-y-4 mb-8">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="h-5 w-5 text-white shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="flex items-center gap-4 pt-6 border-t border-white/20">
                <img
                  src={oneMillionBadge}
                  alt="One Million Dollar Guarantee"
                  className="h-16 w-auto"
                />
                <img
                  src={bbbBadge}
                  alt="BBB Accredited Business"
                  className="h-14 w-auto"
                />
                <img
                  src={soc2Badge}
                  alt="SOC 2 Certified"
                  className="h-14 w-auto"
                />
              </div>
            </div>
            {/* Video Section */}
            <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden p-6 mt-6">
              <h3 className="text-lg font-display font-medium text-foreground mb-4 text-center">
                Need help? Let us guide you through the process
              </h3>
              <div className="rounded-xl overflow-hidden">
                <iframe
                  width="100%"
                  style={{ aspectRatio: "16/9" }}
                  src="https://www.youtube.com/embed/lGrkhc_cZB4?modestbranding=1&rel=0&showinfo=0"
                  title="EquityProtect Sign Up Guide"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </motion.div>

          {/* Right Column - Token Verification */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="adbox_width"
          >
            {/* Verification Card */}
            <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
              {/* Secure Connection Bar */}
              <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
                <Lock className="h-4 w-4 text-white" />
                <span className="text-white font-medium text-sm">
                  Secure connection
                </span>
              </div>

              {/* Verification Form */}
              <div className="p-8 md:p-10 text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center">
                    <Shield className="h-8 w-8 text-secondary" />
                  </div>
                </div>

                <h1 className="text-xl md:text-2xl font-display font-medium text-foreground mb-3">
                  Enter your personal token from Mobile number
                </h1>
                <p className="text-muted-foreground mb-8">
                  Please enter the 6 digit security code we have sent to your
                  mobile phone{" "}
                  <span className="font-medium text-foreground">
                    {maskPhone(phn_num)}
                  </span>
                  .
                </p>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (otp.length !== 6) return;
                    onSubmit?.(e);
                  }}
                  id="signup-verify-phone-form"
                >
                  {/* Hubspot hidden input for cookie breaker */}
                  <HubspotCookieBreakerInput />

                  {/* OTP Input */}
                  <div className="flex justify-center mb-8">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={(value) => handleChange(value)}
                    >
                      <InputOTPGroup className="gap-2 md:gap-3">
                        <InputOTPSlot
                          index={0}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={1}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={2}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={3}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={4}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={5}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  {otpErr?.length > 0 && otpSucess?.length === 0 ? (
                    <p className="text-danger text-sm mb-6">{otpErr}</p>
                  ) : (
                    <p className="text-secondary text-sm mb-6">{otpSucess}</p>
                  )}
                  {/* Verify Button */}
                  <Button
                    type="submit"
                    disabled={otp.length !== 6}
                    className={TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS}
                  >
                    VERIFY MOBILE NUMBER
                  </Button>
                </form>

                {/* Resend Link */}
                {isTimerActive ? (
                  <p className="text-muted-foreground">
                    Resend OTP in
                    <b> {formatTimeForResendOTP(timer)} seconds</b>
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    Did not receive the code?{" "}
                    <button
                      onClick={() => {
                        resendOTPButtonClick("email");
                        startTimer(120);
                      }}
                      className="text-secondary hover:text-secondary/80 font-medium transition-colors"
                    >
                      Get a new one
                    </button>
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </>
    </>
  );
};

export default VerifyPhone;
